import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService, KIOSK_AUTH_TOKEN_CHANGED_EVENT } from '@/services/apiService';
import BroadcastMessageBody from '@/components/BroadcastMessageBody';
import { Button } from '@/components/ui';
import { modalWidthStyle, MODAL_Z } from '@/styles/modalTokens';
import { typeRole, fsClass } from '@/styles/typography';

type PopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

/**
 * In-app popups: `once` uses OK to mark seen on server; `sticky_daily` is re-fetched each load (shows again
 * after reload) until expiresAt; OK only hides for the current page session; Don't show again (server).
 */
export default function KioskInAppPopupGate() {
  const [queue, setQueue] = useState<PopupRow[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [marking, setMarking] = useState(false);
  const batchTotalRef = useRef(0);

  const load = useCallback(async () => {
    // Avoid noisy 401s when user is not authenticated yet.
    // The gate will re-run automatically when the token changes (login/logout).
    if (!apiService.getAccessToken()) {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
      return;
    }
    try {
      const res = await apiService.getMyUnseenInAppPopups();
      if (res.success && res.data?.popups?.length) {
        const list = res.data.popups;
        batchTotalRef.current = list.length;
        setQueue(list);
        setActiveIndex(0);
      } else {
        batchTotalRef.current = 0;
        setQueue([]);
        setActiveIndex(0);
      }
    } catch {
      batchTotalRef.current = 0;
      setQueue([]);
      setActiveIndex(0);
    }
  }, []);

  useEffect(() => {
    void load();
    const onAuth = () => void load();
    window.addEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
    return () => window.removeEventListener(KIOSK_AUTH_TOKEN_CHANGED_EVENT, onAuth);
  }, [load]);

  const current = queue[activeIndex];
  const isSticky = current?.displayMode === 'sticky_daily';

  const removeCurrentFromQueue = () => {
    setQueue((prev) => {
      const next = prev.filter((_, i) => i !== activeIndex);
      setActiveIndex((idx) => {
        if (next.length === 0) return 0;
        return Math.min(idx, next.length - 1);
      });
      return next;
    });
  };

  const onMarkSeen = async () => {
    if (!current || marking || isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupSeen(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  const onCloseSticky = () => {
    if (!current || marking || !isSticky) return;
    removeCurrentFromQueue();
  };

  const onNeverAgain = async () => {
    if (!current || marking || !isSticky) return;
    setMarking(true);
    try {
      const res = await apiService.markInAppPopupNeverAgain(current.id);
      if (res.success) removeCurrentFromQueue();
    } finally {
      setMarking(false);
    }
  };

  if (!current) return null;

  const batchTotal = batchTotalRef.current;
  const positionInBatch = activeIndex + 1;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-md"
      style={{ zIndex: MODAL_Z.popup }}
      role="presentation"
      onPointerDown={(e) => {
        // Block page under the popup; only panel CTAs dismiss.
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="kiosk-in-app-popup-title"
        aria-describedby={batchTotal > 1 ? 'kiosk-in-app-popup-step' : undefined}
        className="cosmos-modal-panel flex max-h-[var(--wf-mdl-max-h)] w-full flex-col overflow-hidden text-ink"
        style={{ ...modalWidthStyle('--wf-mdl-wide-w'), animation: 'kioskPopupEnter 180ms ease-out' }}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <style>{`@keyframes kioskPopupEnter { from { opacity: 0; transform: translateY(8px) scale(0.99); } to { opacity: 1; transform: translateY(0) scale(1); } }`}</style>
        <div className="flex items-start justify-between gap-3 border-b border-border-ice px-7 py-6">
          <h2 id="kiosk-in-app-popup-title" className={`min-w-0 flex-1 truncate ${typeRole.header2} font-semibold leading-8 text-ink`}>
            {current.title || 'Announcement'}
          </h2>
          {batchTotal > 1 ? (
            <p id="kiosk-in-app-popup-step" className={`shrink-0 rounded-md bg-white/8 px-2.5 py-1 ${fsClass.base} font-medium tabular-nums text-caption-muted`}>
              {positionInBatch} of {batchTotal}
            </p>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 cosmos-scroll-y px-7 py-5">
          {isSticky && current.expiresAt ? (
            <p className={`mb-3 ${typeRole.caption} text-caption-muted`}>
              Ends{' '}
              {new Date(current.expiresAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          ) : null}
          <div className={`whitespace-pre-wrap ${typeRole.input} leading-7 text-ink`}>
            <BroadcastMessageBody
              text={current.body}
              linkClassName="font-medium text-cyan-300 underline decoration-cyan-300/70 underline-offset-2 hover:text-cyan-200 break-all"
            />
          </div>
        </div>

        <div className="px-7 pb-6 pt-4">
          {isSticky ? (
            <>
              <Button
                variant="moneyOut"
                size="lg"
                onClick={() => onCloseSticky()}
                disabled={marking}
                className="mx-auto block w-1/3 max-w-full"
              >
                OK
              </Button>
              <div className="mt-3 text-center">
                <Button
                  variant="quiet"
                  size="sm"
                  onClick={() => void onNeverAgain()}
                  disabled={marking}
                  className={`${fsClass.md} text-white/35 underline decoration-white/20 underline-offset-2 transition duration-150 hover:text-white/50 disabled:opacity-40`}
                >
                  Don&apos;t show this again
                </Button>
              </div>
            </>
          ) : (
            <Button
              variant="moneyOut"
              size="lg"
              onClick={() => void onMarkSeen()}
              disabled={marking}
              className="mx-auto block w-1/3 max-w-full"
            >
              {marking ? 'Saving...' : 'OK'}
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
